// externals
import type { Reducer } from 'redux';

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ManualFieldOverridesState } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { LOCKED_PROGRAM_META, actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';

// action types
import {
    CLEAR_MANUAL_FIELD_OVERRIDES,
    SET_AUTO_SELECT_DEALER_DEFAULTS,
    HYDRATE_MANUAL_FIELD_OVERRIDES,
    RESTORE_MANUAL_FIELD_OVERRIDES,
    SET_AUTO_SELECT_CHECKED,
    SET_MANUAL_FIELD_OVERRIDE
} from '../actionTypes/manualFieldOverridesActionTypes';

// constants
import {
    EXPLICIT_LENDER_CARRIED_OVERRIDE_FIELDS,
    MILEAGE_COST_OPTIONS_OVERRIDE_FIELDS
} from '../actions/manualFieldOverridesActionCreators';
import { invalidatesProgram } from '../programInvalidatingActions';

export const manualFieldOverridesInitialState: ManualFieldOverridesState = {
    fields: []
};

// US2105291: the acquisition fee and the Mileage & Cost Options fields (DE773720) are the overrides that
// survive an automatic re-price; every other field's override is replaced by the program's value. This records only that the dealer overrode it —
// whether the number on screen is *still* theirs is answered by `isAcqFeeStillDealerEntered`, which
// compares it against the selected term's fee, because the places that replace the fee key on request state
// rather than on the action. A deal type change is excluded here because it moves the deal to a different
// type rather than re-pricing it.
const OVERRIDES_SURVIVING_WITHIN_PROGRAM_RECALC: ReadonlySet<string> = new Set(['acqFee', ...MILEAGE_COST_OPTIONS_OVERRIDE_FIELDS]);

const OVERRIDES_SURVIVING_LOCKED_PROGRAM_RECALC: ReadonlySet<string> = new Set([
    ...OVERRIDES_SURVIVING_WITHIN_PROGRAM_RECALC,
    ...EXPLICIT_LENDER_CARRIED_OVERRIDE_FIELDS
]);

const survivingOverridesFor = (action: AnyFSA): ReadonlySet<string> =>
    action.meta?.[LOCKED_PROGRAM_META] ? OVERRIDES_SURVIVING_LOCKED_PROGRAM_RECALC : OVERRIDES_SURVIVING_WITHIN_PROGRAM_RECALC;

export type ManualFieldOverridesReducer = Reducer<ManualFieldOverridesState>;

export const reducer: ManualFieldOverridesReducer = (
    state: ManualFieldOverridesState = manualFieldOverridesInitialState,
    action: AnyFSA
) => {
    // `checkedByOfferType` is spread through below: none of these actions is the user changing the selection.
    if (invalidatesProgram(action)) {
        if (!state.fields.length) return state;
        // A deal type change is a mode-level move, so it clears everything; the rest are automatic re-prices,
        // which the acquisition fee's override survives.
        const surviving = survivingOverridesFor(action);
        const fields =
            action.type === offerReduxActionTypes.UPDATED_OFFER_TYPE ? [] : state.fields.filter((field) => surviving.has(field));
        // Same reference-equality reason as the hydrate below: the scoped store caches each scope's state on
        // root-state reference equality, so a no-op must return the same object.
        return fields.length === state.fields.length ? state : { ...state, fields };
    }

    switch (action.type) {
        case SET_MANUAL_FIELD_OVERRIDE: {
            const fieldKey = action.payload as string;
            if (state.fields.includes(fieldKey)) return state;
            return { ...state, fields: [...state.fields, fieldKey] };
        }
        case RESTORE_MANUAL_FIELD_OVERRIDES: {
            const fields = action.payload as string[];
            if (fields.length === state.fields.length && fields.every((field, index) => field === state.fields[index])) {
                return state;
            }
            return { ...state, fields };
        }
        case CLEAR_MANUAL_FIELD_OVERRIDES: {
            if (!state.fields.length) return state;
            const clearOnly = action.payload as string[] | undefined;
            if (!clearOnly) return { ...state, fields: [] };
            const fields = state.fields.filter((field) => !clearOnly.includes(field));
            return fields.length === state.fields.length ? state : { ...state, fields };
        }
        case SET_AUTO_SELECT_CHECKED: {
            // Also carries a value read from baggage, so validate it.
            const { offerType, checked, replaceOtherOfferTypes } = action.payload ?? {};
            if (!offerType || typeof checked !== 'boolean') return state;
            const existing = state.checkedByOfferType;
            // Same reference-equality reason as the hydrate below, for the replace case: a load that lands on
            // the map it already had must not invalidate every scope's cache.
            if (replaceOtherOfferTypes) {
                if (existing && Object.keys(existing).length === 1 && existing[offerType as OfferType] === checked) {
                    return state;
                }
                return { ...state, checkedByOfferType: { [offerType]: checked } };
            }
            if (existing?.[offerType as OfferType] === checked) return state;
            return { ...state, checkedByOfferType: { ...existing, [offerType]: checked } };
        }
        case SET_AUTO_SELECT_DEALER_DEFAULTS: {
            // Replaces rather than merges: the snapshot describes one deal.
            return { ...state, dealerDefaultByOfferType: action.payload };
        }
        case HYDRATE_MANUAL_FIELD_OVERRIDES: {
            // persisted baggage, so the payload is only as trustworthy as whatever last wrote the deal
            const persisted = Array.isArray(action.payload) ? action.payload : [];
            const fields = persisted.filter((field): field is string => typeof field === 'string');
            // Every load hydrates, so keep the same object when there is nothing to change: the scoped store
            // caches each scope's state on root-state reference equality, so a no-op hydrate that returned a
            // new object would invalidate every scope's cache, not just this one.
            if (!fields.length && !state.fields.length) return state;
            return { ...state, fields };
        }
        default: {
            return state;
        }
    }
};
